import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
  startViewTransition,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents, QuantitySelectorUpdateEvent, CartSectionRestoredEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from '@shopify/events';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  /** @type {Set<Promise<void>>} */
  static #pendingQuantityUpdates = new Set();

  /**
   * Includes quantity mutations, their section renders, and control cleanup across all cart instances.
   * @returns {ReadonlyArray<Promise<void>>} A snapshot that cannot modify the shared pending set.
   */
  static get pendingQuantityUpdates() {
    return Array.from(CartItemsComponent.#pendingQuantityUpdates);
  }

  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent) this.#onQuantityChange(event);
    },
    300
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** @type {WeakMap<Event, Promise<boolean>>} */
  #externalCartUpdatePromises = new WeakMap();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /**
   * Refreshes this section when a cart AJAX response cannot include rendered section HTML.
   * @param {Object} [options] - Additional section morph options.
   * @param {boolean} [options.injectStylesheet]
   */
  async #refreshCartSection(options = {}, notify = true) {
    try {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        ...options,
      });
      this.#updateCartQuantitySelectorButtonStates();
      return true;
    } catch (error) {
      if (error?.name === 'AbortError') return false;

      if (notify) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to refresh cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
      return false;
    }
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const isExternalCartUpdate = this.#isExternalCartUpdate(event);

    event.promise
      ?.then(({ detail }) => {
        if (!isExternalCartUpdate) return;

        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (detail?.sections);
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          sectionRenderer.abortRender(this.sectionId);
          morphSection(this.sectionId, sectionsHtml, { mode: this.isDrawer ? 'hydration' : 'full' });
        } else {
          this.#refreshCartSection();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.addEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.addEventListener('pageshow', this.#handlePageShow);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    document.removeEventListener(StandardEvents.cartDiscountUpdate, this.#handleDiscountUpdate);
    document.removeEventListener(StandardEvents.cartNoteUpdate, this.#handleNoteUpdate);
    window.removeEventListener('pageshow', this.#handlePageShow);
  }

  /**
   * Re-renders the cart section when a page is restored from browser history.
   * Normal navigations are ignored, so this adds no request to ordinary page views.
   *
   * @param {PageTransitionEvent} event
   */
  #handlePageShow = (event) => {
    const navigationEntry = globalThis.performance?.getEntriesByType?.('navigation')[0];
    const navigationType = CartItemsComponent.#isNavigationTiming(navigationEntry) ? navigationEntry.type : undefined;
    if (!event.persisted && navigationType !== 'back_forward') return;
    if (!this.dataset.sectionId) return;

    sectionRenderer
      .renderSection(this.sectionId, {
        cache: false,
        mode: this.isDrawer ? 'hydration' : 'full',
        injectStylesheet: this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null,
      })
      .then(() => {
        this.#updateCartQuantitySelectorButtonStates();
        this.dispatchEvent(new CartSectionRestoredEvent());
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-items] history restore render failed:', error);
      });
  };

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // If the cart item row is the last row, optimistically trigger the cart empty state
    const isEmptyCart = rowsToRemove.length == this.refs.cartItemRows.length;

    const template = document.getElementById('empty-cart-template');
    if (isEmptyCart && template instanceof HTMLTemplateElement) {
      const clone = document.importNode(template.content, true);

      startViewTransition(() => {
        document.getElementById('cart-drawer-heading')?.remove();
        this.replaceChildren(clone);
      }, [this.isDrawer ? 'empty-cart-drawer' : 'empty-cart-page']);

      return;
    }

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  async updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    let completeQuantityUpdate = () => {};
    /** @type {Promise<void>} */
    const quantityUpdate = new Promise((resolve) => {
      completeQuantityUpdate = () => resolve();
    });
    CartItemsComponent.#pendingQuantityUpdates.add(quantityUpdate);
    if (this.refs.cartMutationError instanceof HTMLElement) this.refs.cartMutationError.hidden = true;
    for (const container of this.querySelectorAll('[data-cart-inline-recovery]')) {
      container.removeAttribute('data-cart-inline-recovery');
      container.removeAttribute('data-cart-inline-policy');
      container.querySelector('[data-cart-inline-recovery-link]')?.remove();
      container.classList.add('hidden');
    }
    let cartUpdateSettled = false;
    // Capture the line key before any asynchronous response can reorder or replace the rows.
    const originalRow = this.refs.cartItemRows?.[config.line - 1];
    const lineId = originalRow?.dataset.key ?? '';
    // Snapshot the rendered cart quantity before the request; don't read the
    // editable input, which already contains the requested quantity.
    const originalRaw = originalRow?.dataset.cartOriginalQuantity;
    const originalQuantity = typeof originalRaw === 'string' && /^[1-9]\d*$/.test(originalRaw)
      ? Number(originalRaw) : null;
    const reconcileFailure = async (quantityRejected = false) => {
      resetShimmer(this);
      const [cartResult, refreshResult] = await Promise.allSettled([
        this.fetchCartData({ fresh: true }),
        this.#refreshCartSection({}, false),
      ]);
      const authoritativeCart = cartResult.status === 'fulfilled' ? cartResult.value : null;
      const refreshed = refreshResult.status === 'fulfilled' && refreshResult.value === true;
      const activeOwner = Array.from(document.querySelectorAll('cart-items-component')).find(
        (component) => component.dataset.sectionId === this.dataset.sectionId &&
          component.hasAttribute('data-drawer') === this.hasAttribute('data-drawer')
      );
      const errorOwner = activeOwner instanceof CartItemsComponent ? activeOwner : this.isConnected ? this : null;
      const matchingItems = Array.isArray(authoritativeCart?.items) && lineId
        ? authoritativeCart.items.filter((item) => item?.key === lineId)
        : [];
      const currentQuantity = matchingItems.length === 1 ? matchingItems[0].quantity : null;
      const hasUnfulfilledIncrease = quantityRejected && refreshed &&
        Number.isSafeInteger(config.quantity) && Number.isSafeInteger(currentQuantity) &&
        Number.isSafeInteger(originalQuantity) && currentQuantity > 0 &&
        config.quantity > originalQuantity && config.quantity > currentQuantity;
      const code = authoritativeCart && refreshed ? 'INVALID' : 'SERVICE_UNAVAILABLE';
      if (errorOwner) {
        errorOwner.#showCartMutationError(
          hasUnfulfilledIncrease,
          quantityRejected && refreshed && matchingItems.length === 1 ? lineId : '',
          code,
          config.quantity,
          currentQuantity
        );
      } else {
        // The old component may have been removed during a failed morph. Report
        // once at the connected document instead of dispatching on a detached node.
        document.dispatchEvent(new CartErrorEvent({
          error: this.dataset.cartUpdateError || 'Cart update failed',
          code,
        }));
      }
      if (cartUpdateSettled) return;
      if (authoritativeCart && refreshed) {
        let cart;
        try {
          cart = CartLinesUpdateEvent.createCartFromAjaxResponse(authoritativeCart);
        } catch (_) {
          cartUpdateSettled = true;
          deferredUpdatePromise.reject(new Error('Cart update could not be confirmed'));
          return;
        }
        cartUpdateSettled = true;
        deferredUpdatePromise.resolve({
          cart,
          detail: {
            items: authoritativeCart.items,
            itemCount: authoritativeCart.item_count,
            source: 'cart-items-component',
            didError: true,
          },
        });
      } else {
        cartUpdateSettled = true;
        deferredUpdatePromise.reject(new Error('Cart update could not be confirmed'));
      }
    };

    try {
      this.#disableCartItems();

      const { line, quantity } = config;
      const { cartTotal } = this.refs;

      const cartItemsComponents = document.querySelectorAll('cart-items-component');
      const sectionsToUpdate = new Set([this.sectionId]);
      for (const item of cartItemsComponents) {
        if (item instanceof HTMLElement && item.dataset.sectionId) {
          sectionsToUpdate.add(item.dataset.sectionId);
        }
      }

      const presentationMarker = document.querySelector('[data-cart-free-shipping-success-configured]');
      const presentationSection = presentationMarker?.closest('.shopify-section');
      const presentationSectionId = presentationSection?.id.replace(/^shopify-section-/, '');
      if (presentationSectionId) sectionsToUpdate.add(presentationSectionId);

      for (const sectionId of sectionsToUpdate) {
        sectionRenderer.abortRender(sectionId);
      }

      const body = JSON.stringify({
        line,
        quantity,
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      cartTotal?.shimmer();

      this.dispatchEvent(
        new CartLinesUpdateEvent({
          action: config.action === 'change' && quantity > 0 ? 'update' : 'remove',
          context: 'cart',
          lines: [{ id: lineId, quantity }],
          promise: deferredUpdatePromise.promise,
        })
      );

      const response = await fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }));
      const responseText = await response.text();
      let parsedResponseText;
      try {
        parsedResponseText = JSON.parse(responseText);
      } catch (error) {
        if (response.ok || config.action !== 'change' || quantity <= 0) throw error;
        parsedResponseText = {};
      }

      resetShimmer(this);

      if (config.action === 'change' && quantity > 0 &&
          (!response.ok || parsedResponseText.errors || Number(parsedResponseText.status) >= 400)) {
        const quantityRejected = response.status === 422 ||
          (response.status === 200 && typeof parsedResponseText.status === 'number' && parsedResponseText.status === 422);
        await reconcileFailure(quantityRejected);
        return;
      }

      if (parsedResponseText.errors) {
        this.#handleCartError(line, parsedResponseText);
        deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
        return;
      }

      const sections = /** @type {Record<string, string> | null | undefined} */ (parsedResponseText.sections);
      const sectionHTML = sections?.[this.sectionId];
      const parsedItemCount = Number(parsedResponseText.item_count);
      let newCartItemCount = Number.isFinite(parsedItemCount) ? parsedItemCount : 0;

      this.#updateQuantitySelectors(parsedResponseText);

      if (!sectionHTML) {
        const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText);
        cartUpdateSettled = true;
        deferredUpdatePromise.resolve({
          cart,
          detail: {
            sections: sections ?? undefined,
            items: parsedResponseText.items,
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            didError: false,
          },
        });

        await this.#refreshCartSection();
        return;
      }

      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : newCartItemCount;

      const cart = CartLinesUpdateEvent.createCartFromAjaxResponse(parsedResponseText);
      cartUpdateSettled = true;
      deferredUpdatePromise.resolve({
        cart,
        detail: {
          sections: sections ?? undefined,
          items: parsedResponseText.items,
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          didError: false,
        },
      });

      sectionRenderer.abortRender(this.sectionId);
      await morphSection(this.sectionId, sectionHTML, {
        mode: this.isDrawer ? 'hydration' : 'full',
      });

      this.#updateCartQuantitySelectorButtonStates();
    } catch (error) {
      console.error(error);
      if (config.action === 'change' && config.quantity > 0) {
        await reconcileFailure();
      } else {
        deferredUpdatePromise.reject(error);
        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || 'Failed to update cart',
            code: 'SERVICE_UNAVAILABLE',
          })
        );
      }
    } finally {
      try {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      } finally {
        CartItemsComponent.#pendingQuantityUpdates.delete(quantityUpdate);
        completeQuantityUpdate();
      }
    }
  }

  #showCartMutationError(quantityRejected = false, lineId = '', code = 'INVALID', requestedQuantity = null, currentQuantity = null) {
    const status = this.refs.cartMutationError;
    const text = this.refs.cartMutationErrorText;
    if (!(status instanceof HTMLElement) || !(text instanceof HTMLElement)) {
      this.dispatchEvent(new CartErrorEvent({
        error: this.dataset.cartUpdateError || 'Cart update failed',
        code,
      }));
      return;
    }
    if (!this.hasAttribute('data-drawer') && this.contains(status)) {
      const page = this.querySelector('.cart-page');
      const title = page?.querySelector(':scope > .cart-page__title');
      if (page instanceof HTMLElement && page.closest('cart-items-component') === this &&
          title instanceof HTMLElement && title.parentElement === page) {
        title.after(status);
      }
    }
    const contact = this.refs.cartMutationContact;
    const viewCart = this.refs.cartMutationViewCart;
    const rows = lineId ? Array.from(this.querySelectorAll('[ref="cartItemRows[]"]')).filter(
      (row) => row instanceof HTMLElement && row.dataset.key === lineId && row.closest('cart-items-component') === this
    ) : [];
    // This flag comes from the freshly rendered Shopify variant, and indicates
    // availability only; it does not assert why the change was rejected.
    const unavailable = rows.length === 1 && rows[0].dataset.cartVariantAvailable === 'false';
    const policyRaw = rows.length === 1 ? rows[0].dataset.cartPolicyLimit : null;
    const policyLimit = typeof policyRaw === 'string' && /^[1-9]\d*$/.test(policyRaw) ? Number(policyRaw) : null;
    // The message describes a merchant-maintained policy, not the cause of 422.
    // Missing/invalid settings, exceptions and incomplete context use the generic message.
    const showPolicy = !unavailable && quantityRejected && Number.isSafeInteger(policyLimit) &&
      Number.isSafeInteger(requestedQuantity) && requestedQuantity > policyLimit &&
      Number.isSafeInteger(currentQuantity) && currentQuantity === policyLimit && Boolean(this.dataset.cartPolicyMessage);
    const showContact = !unavailable && quantityRejected && contact instanceof HTMLElement;
    text.textContent = (unavailable && this.dataset.cartVariantUnavailable) ||
      (showPolicy && this.dataset.cartPolicyMessage) ||
      (showContact && this.dataset.cartQuantityRejected) || this.dataset.cartUpdateError || '';
    if (contact instanceof HTMLElement) contact.hidden = !showContact;
    if (viewCart instanceof HTMLElement) viewCart.hidden = showContact;
    const container = rows.length === 1 ? rows[0].querySelector('.cart-items__error') : null;
    const inlineText = container?.querySelector('.cart-item__error-text');
    const inlineAlert = container?.querySelector('[role="alert"]');
    const recoveryLink = showContact ? contact : viewCart;
    const canUseInline = container instanceof HTMLElement && container.classList.contains('hidden') &&
      inlineText instanceof HTMLElement && inlineAlert instanceof HTMLElement && recoveryLink instanceof HTMLAnchorElement;
    if (canUseInline) {
      const link = recoveryLink.cloneNode(true);
      link.removeAttribute('ref');
      link.removeAttribute('hidden');
      link.setAttribute('data-cart-inline-recovery-link', '');
      inlineText.textContent = text.textContent;
      inlineText.append(' ', link);
      container.setAttribute('data-cart-inline-recovery', '');
      if (showPolicy) container.setAttribute('data-cart-inline-policy', '');
      else container.removeAttribute('data-cart-inline-policy');
      container.classList.remove('hidden');
      status.hidden = true;
    } else {
      status.hidden = false;
    }
    this.dispatchEvent(
      new CartErrorEvent({
        error: text.textContent || 'Cart update failed',
        code,
      })
    );
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || 'Cart update failed',
        code: 'INVALID',
      })
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    const updatePromise =
      event.promise
        ?.then(async ({ detail }) => {
          const sections = detail?.sections;
          const cartItemsHtml = sections?.[this.sectionId];
          // Animate empty → non-empty in the drawer (possible in squeeze mode
          // where the page is interactive alongside the open drawer). This also
          // needs the response stylesheet because it adds the cart summary markup.
          const wasEmptyCartDrawer = this.isDrawer && this.querySelector('[data-cart-drawer-empty]') !== null;
          /** @type {'hydration' | 'full'} */
          const mode = this.isDrawer ? 'hydration' : 'full';
          const morphOptions = {
            mode,
            injectStylesheet: wasEmptyCartDrawer,
          };

          if (cartItemsHtml) {
            sectionRenderer.abortRender(this.sectionId);
            const existingKeys = new Set(this.refs.cartItemRows?.map((row) => row.dataset.key) ?? []);

            if (wasEmptyCartDrawer) {
              await startViewTransition(
                () => morphSection(this.sectionId, cartItemsHtml, morphOptions),
                ['fill-cart-drawer']
              );
            } else {
              await morphSection(this.sectionId, cartItemsHtml, morphOptions);
            }

            if (!wasEmptyCartDrawer && !prefersReducedMotion()) {
              for (const row of this.refs.cartItemRows ?? []) {
                if (!existingKeys.has(row.dataset.key)) {
                  row.classList.add('adding');
                  onAnimationEnd(row, () => row.classList.remove('adding'));
                }
              }
            }

            this.#updateCartQuantitySelectorButtonStates();
          } else {
            await this.#refreshCartSection(morphOptions);
          }

          return true;
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[cart-items] Event promise rejected:', error);
          return false;
        }) ?? Promise.resolve(false);

    this.#externalCartUpdatePromises.set(event, updatePromise);
  };

  /**
   * Resolves when this cart instance has applied the exact external cart-lines event.
   * @param {Event} event
   * @returns {Promise<boolean>}
   */
  whenExternalCartUpdateSettled(event) {
    return this.#externalCartUpdatePromises.get(event) ?? Promise.resolve(false);
  }

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData({ fresh = false } = {}) {
    if (!fresh && this.#pendingCartFetch) return this.#pendingCartFetch;
    const request = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status} ${response.statusText}`);
      const data = await response.json();
      if (fresh && (!Array.isArray(data?.items) || !Number.isFinite(Number(data?.item_count)))) {
        throw new Error('Invalid cart response');
      }
      return data;
    })();
    if (fresh) return request;
    this.#pendingCartFetch = request.finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }

  /**
   * @param {PerformanceEntry | undefined} entry
   * @returns {entry is PerformanceNavigationTiming}
   */
  static #isNavigationTiming(entry) {
    return entry?.entryType === 'navigation';
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

